import React, { useEffect, useRef, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import axiosInstance from "../../../redux/api";
import "./OutgoingInvoiceEmails.css";
import "./InvoiceEmailSenders.css";

const placeholders = ["receiver_name", "document_type", "invoice_number", "total_amount", "vat_amount", "invoice_date"];
async function api(path, payload) {
  try {
    const r = payload ? await axiosInstance.post(path, payload, {timeout: 45000}) : await axiosInstance.get(path, {timeout: 45000});
    if (!r.data.status) throw new Error(r.data.message);
    return r.data.response;
  } catch (e) { throw new Error(e.response?.data?.message || e.message || "Unable to update settings."); }
}
function Editor({ userId, close, updated }) {
  const dialog = useRef(null);
  const [state, setState] = useState(null), [details, setDetails] = useState({}), [replyInput, setReplyInput] = useState(""),
    [busy, setBusy] = useState(false), [error, setError] = useState(""), [message, setMessage] = useState(""),
    [code, setCode] = useState(false), [preview, setPreview] = useState(null), [changeEmail, setChangeEmail] = useState(false);
  const path = "invoice_email/senders/" + userId;
  function hydrate(data) {
    setState(data); setDetails({...data.details, ...data.draft?.details}); setReplyInput(""); setChangeEmail(false);
  }
  useEffect(() => {
    dialog.current.showModal();
    api(path).then(hydrate).catch(e => setError(e.message));
  }, [path]);
  const edit = (key, value) => setDetails(old => ({...old, [key]: value}));
  function replies() {
    const additions = replyInput.trim().split(/[\s,;]+/).filter(Boolean);
    const values = [...new Set([...(details.reply_to || []), ...additions])];
    const domain = details.sender_email?.split("@")[1]?.toLowerCase();
    if (values.length > 10 || values.some(value => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) || value.split("@")[1].toLowerCase() !== domain)) {
      setError("Enter up to 10 Reply-To addresses using the sender's domain."); return null;
    }
    edit("reply_to", values); setReplyInput(""); return values;
  }
  async function act(action) {
    const values = replies(); if (!values) return;
    setBusy(true); setError(""); setMessage("");
    try {
      const result = await api(path, {action, revision: state.revision, draft_revision: state.draft?.revision || 0,
        details: {...details, reply_to: values}});
      if (action === "preview") setPreview(result);
      else { hydrate(result); setMessage("Saved"); updated(); }
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  }
  return <dialog ref={dialog} className="outgoing-email-modal sender-config-modal" onCancel={close} onClose={close}>
    <header><h3>Invoice email sender · User {userId}</h3><button onClick={close} aria-label="Close">×</button></header>
    {error && <p role="alert">{error}</p>}
    {!state ? <p>Loading…</p> : <>
      <div className="sender-config-status"><strong>{state.status}</strong><span>{state.domain}</span>
        {state.draft && <small>Includes saved setup progress</small>}</div>
      <fieldset disabled={busy}>
        <label>Sender email<input value={details.sender_email || ""} disabled={!!state.domain && !changeEmail}
          placeholder="email@domain.com" onChange={e => edit("sender_email", e.target.value)} /></label>
        {!!state.domain && <button type="button" onClick={() => setChangeEmail(true)}>Change sender email</button>}
        {changeEmail && <small>Changing the email restarts setup. A new domain must be verified.</small>}
        <label>Sender name<input value={details.sender_name || ""} onChange={e => edit("sender_name", e.target.value)} /></label>
        <label>Reply-To addresses<div className="sender-config-replies">
          {(details.reply_to || []).map(address => <span key={address}>{address}<button type="button" aria-label={"Remove " + address}
            onClick={() => edit("reply_to", details.reply_to.filter(x => x !== address))}>×</button></span>)}
          <input value={replyInput} placeholder="email@domain.com" onChange={e => setReplyInput(e.target.value)}
            onBlur={() => {if (replyInput.trim()) replies();}} onKeyDown={e => {
              if (["Enter", " ", ","].includes(e.key)) {e.preventDefault(); replies();}
              else if (e.key === "Backspace" && !replyInput) edit("reply_to", details.reply_to.slice(0, -1));
            }} />
        </div></label>
        <label>Email subject<input value={details.subject || ""} placeholder="Your {document_type} is ready" onChange={e => edit("subject", e.target.value)} /></label>
        <div className="sender-config-editor-heading"><label>Email body</label><button type="button" onClick={() => setCode(!code)}>{code ? "Visual" : "HTML"}</button></div>
        {code ? <textarea aria-label="Email body HTML" rows={10} value={details.body_html || ""} onChange={e => edit("body_html", e.target.value)} /> :
          <ReactQuill theme="snow" value={details.body_html || ""} readOnly={busy} onChange={value => edit("body_html", value)} />}
        <div className="sender-config-placeholders">{placeholders.map(key => <code key={key}>{"{" + key + "}"}</code>)}</div>
      </fieldset>
      <div className="sender-config-actions"><button disabled={busy} onClick={() => act("preview")}>Preview</button>
        <span role="status">{message}</span><button disabled={busy} onClick={() => act(state.active && !changeEmail ? "save_active" : details.sender_email ? "save" : "save_template")}>Save</button></div>
      {!!state.domain && <div className="sender-config-actions">
        <button disabled={busy} onClick={() => act("disable")}>Send from Simple Fatoora</button>
        <button disabled={busy} onClick={() => act("verify")}>Check verification</button>
        <button disabled={busy || !state.verified} onClick={() => act("test")}>Send test</button>
        {!state.active && <button disabled={busy || !state.verified || state.test_status !== "delivered"} onClick={() => act("activate")}>Activate</button>}
      </div>}
      {!state.active && state.records?.length > 0 && <details><summary>DNS records</summary>{state.records.map((r, i) => <p key={i}><strong>{r.type} · {r.status}</strong><code>{r.name}</code><code>{r.value}</code></p>)}</details>}
    </>}
    {preview && <div className="sender-preview-overlay" role="dialog" aria-modal="true" aria-label="Email preview">
      <header><strong>{preview.subject}</strong><button onClick={() => setPreview(null)}>Close preview</button></header>
      <iframe title="Email preview" sandbox="" srcDoc={"<meta http-equiv=\"Content-Security-Policy\" content=\"default-src 'none'; style-src 'unsafe-inline'\">" + preview.html} />
    </div>}
  </dialog>;
}
export default function InvoiceEmailSenders() {
  const [data, setData] = useState({items: [], page: 1, pages: 1, total: 0}), [user, setUser] = useState(""),
    [busy, setBusy] = useState(false), [error, setError] = useState(""), [editing, setEditing] = useState(null);
  async function load(page = 1) {
    setBusy(true); setError("");
    try { setData(await api("invoice_email/senders?page=" + page + (user ? "&user_id=" + encodeURIComponent(user) : ""))); }
    catch(e) {setError(e.message);} finally {setBusy(false);}
  }
  useEffect(() => {load();}, []); // eslint-disable-line react-hooks/exhaustive-deps
  return <div className="outgoing-emails-page"><div className="category-wrapper">
    <header><h3>Invoice email senders</h3><button disabled={busy} onClick={() => load(data.page)}>Refresh</button></header>
    <form onSubmit={e => {e.preventDefault(); load();}}><label>User ID<input type="number" min="1" value={user} onChange={e => setUser(e.target.value)} placeholder="All users" /></label><button disabled={busy}>Search</button></form>
    {error && <p role="alert">{error}</p>}
    <div className="outgoing-emails-table"><table><thead><tr><th>User</th><th>Sender</th><th>Reply-To</th><th>Status</th><th>Subject</th><th>Settings</th></tr></thead>
      <tbody>{data.items.map(row => <tr key={row.user_id}><td><strong>{row.user}</strong><small>ID {row.user_id}</small></td>
        <td>{row.details.sender_name}<small>{row.details.sender_email || "Simple Fatoora"}</small></td><td>{row.details.reply_to?.join(", ")}</td>
        <td>{row.status}{row.has_draft && <small>Setup progress saved</small>}</td><td>{row.details.subject}</td><td><button onClick={() => setEditing(row.user_id)}>View / edit</button></td></tr>)}
        {!data.items.length && <tr><td colSpan="6">{busy ? "Loading…" : "No configured senders yet."}</td></tr>}</tbody></table></div>
    <footer><button disabled={busy || data.page <= 1} onClick={() => load(data.page - 1)}>Previous</button><span>{data.total} users · Page {data.page} of {data.pages}</span><button disabled={busy || data.page >= data.pages} onClick={() => load(data.page + 1)}>Next</button></footer>
  </div>{editing && <Editor userId={editing} close={() => setEditing(null)} updated={() => load(data.page)} />}</div>;
}
